import maplibregl from "maplibre-gl";
import { Capacitor } from "@capacitor/core";
import { FetchSource, PMTiles, Protocol } from "pmtiles";
import { NativePmtilesSource } from "./nativePmtilesSource.js";
import {
  PMTILES_CACHE,
  RANGE_CACHE_LAST_USED_HEADER,
  touchMapCache,
} from "./mapCache.js";

export const MAP_STYLE_KEY = "trekkin_map_style";
export const MAP_SOURCE_MODE_KEY = "trekkin_map_source_mode_v1";
export const MAP_SOURCE_MODES = ["auto", "local", "remote"];
const BASE_URL = import.meta.env?.BASE_URL || "/";
const PMTILES_BASE_ENV = import.meta.env?.VITE_PMTILES_BASE_URL || "";
const NATIVE_PMTILES_BASE = "https://www.trekkin.pt/app/";
const PMTILES_RELEASE =
  import.meta.env?.VITE_PMTILES_RELEASE || "2026-07-26-1";
const isNative = typeof Capacitor !== "undefined" && Capacitor.isNativePlatform?.() === true;
const nativePlatform = typeof Capacitor !== "undefined" ? Capacitor.getPlatform?.() : "";
const isIOSNative = isNative && nativePlatform === "ios";
const PMTILES_BASE = PMTILES_BASE_ENV || (isNative ? NATIVE_PMTILES_BASE : BASE_URL);
const NORMALIZED_BASE_URL = PMTILES_BASE.endsWith("/") ? PMTILES_BASE : `${PMTILES_BASE}/`;
const versionedPmtilesUrl = (fileName) =>
  `${NORMALIZED_BASE_URL}tiles/${fileName}?v=${encodeURIComponent(PMTILES_RELEASE)}`;
export const LITE_PMTILES_URL = versionedPmtilesUrl("portugal-lite.pmtiles");
export const PRO_PMTILES_URL = versionedPmtilesUrl("portugal-pro.pmtiles");
export const CONTOURS_PMTILES_URL = versionedPmtilesUrl("portugal-contours.pmtiles");
export const PMTILES_SIZE_HINTS_DEFAULT = {
  [LITE_PMTILES_URL]: 74614782,
  [PRO_PMTILES_URL]: 788571752,
  [CONTOURS_PMTILES_URL]: 118793865,
};
export const LOCAL_PM_LITE_KEY = "trekkin_pmtiles_local_lite_url_v1";
export const LOCAL_PM_PRO_KEY = "trekkin_pmtiles_local_pro_url_v1";
export const LOCAL_PM_CONTOURS_KEY = "trekkin_pmtiles_local_contours_url_v1";
export const MAPS_CLEARED_LOCAL_KEY = "trekkin_pmtiles_local_cleared_at_v1";
export const FORCE_REMOTE_LITE_KEY = "trekkin_force_remote_lite_v1";
export const FORCE_REMOTE_PRO_KEY = "trekkin_force_remote_pro_v1";
export const REMOTE_ONLY_MAPS_KEY = "trekkin_maps_remote_only_v1";
export const RUNTIME_MAP_STYLE_OVERRIDE_KEY = "trekkin_runtime_map_style_override_v1";
export const RUNTIME_DISABLE_CONTOURS_LITE_KEY = "trekkin_runtime_disable_contours_lite_v1";
export const RUNTIME_DISABLE_CONTOURS_PRO_KEY = "trekkin_runtime_disable_contours_pro_v1";
const NORMALIZED_ASSET_BASE_URL = BASE_URL.endsWith("/") ? BASE_URL : `${BASE_URL}/`;
const LOCAL_GLYPHS_URL = `${NORMALIZED_ASSET_BASE_URL}glyphs/{fontstack}/{range}.pbf`;
const IOS_LOCAL_MAP_URLS = {
  [LOCAL_PM_LITE_KEY]: "native-map://portugal-lite.pmtiles",
  [LOCAL_PM_PRO_KEY]: "native-map://portugal-pro.pmtiles",
  [LOCAL_PM_CONTOURS_KEY]: "native-map://portugal-contours.pmtiles",
};
export const PORTUGAL_PMTILES_BOUNDS = [-10.1, 36.7, -5.8, 43.95];
const PORTUGAL_PMTILES_MIN_ZOOM = 6;

const NATIVE_LITE_READY_KEY = "trekkin_lite_native_ready_v1";
const NATIVE_PRO_READY_KEY = "trekkin_pro_native_ready_v1";
const NATIVE_LITE_BYTES_KEY = "trekkin_lite_native_bytes_v1";
const NATIVE_PRO_BYTES_KEY = "trekkin_pro_native_bytes_v1";
const NATIVE_CONTOURS_BYTES_KEY = "trekkin_contours_native_bytes_v1";
const MAP_MIN_REQUIRED_FILE_BYTES_HARD = 5 * 1024 * 1024;
const MAP_MIN_COMPLETE_RATIO = 0.98;
const NATIVE_LOCAL_HINTS = {
  [LOCAL_PM_LITE_KEY]: {
    readyKey: NATIVE_LITE_READY_KEY,
    bytesKey: NATIVE_LITE_BYTES_KEY,
    url: LITE_PMTILES_URL,
  },
  [LOCAL_PM_PRO_KEY]: {
    readyKey: NATIVE_PRO_READY_KEY,
    bytesKey: NATIVE_PRO_BYTES_KEY,
    url: PRO_PMTILES_URL,
  },
  [LOCAL_PM_CONTOURS_KEY]: {
    readyKey: null,
    bytesKey: NATIVE_CONTOURS_BYTES_KEY,
    url: CONTOURS_PMTILES_URL,
  },
};

export function readMapSourceModePref() {
  if (typeof window === "undefined") return "auto";
  try {
    const stored = String(window.localStorage.getItem(MAP_SOURCE_MODE_KEY) || "")
      .trim()
      .toLowerCase();
    if (MAP_SOURCE_MODES.includes(stored)) return stored;
    // Preserve the meaning of the previous device-only remote flag.
    if (window.localStorage.getItem(REMOTE_ONLY_MAPS_KEY) === "1") return "remote";
  } catch {}
  return "auto";
}

function minNativeBytesForHint(hint) {
  const expected = Number(PMTILES_SIZE_HINTS_DEFAULT[hint?.url] || 0);
  if (!Number.isFinite(expected) || expected <= 0) {
    return MAP_MIN_REQUIRED_FILE_BYTES_HARD;
  }
  return Math.max(
    MAP_MIN_REQUIRED_FILE_BYTES_HARD,
    Math.floor(expected * MAP_MIN_COMPLETE_RATIO)
  );
}

function hasNativeMapRuntimeHint() {
  if (typeof window === "undefined") return false;
  try {
    return (
      isNative ||
      typeof window?.webkit?.messageHandlers?.nativeBridge?.postMessage === "function" ||
      window?.Capacitor?.isNativePlatform?.() === true
    );
  } catch {
    return isNative;
  }
}

function readStoredNativeBytes(key) {
  try {
    const n = Number(window.localStorage.getItem(key) || 0);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function hasNativeLocalHint(key) {
  if (!hasNativeMapRuntimeHint()) return false;
  try {
    if (window.localStorage.getItem(MAPS_CLEARED_LOCAL_KEY)) return false;
  } catch {}
  const hint = NATIVE_LOCAL_HINTS[key];
  if (!hint) return false;
  try {
    const storedUrl = String(window.localStorage.getItem(key) || "").trim();
    const storedBytes = readStoredNativeBytes(hint.bytesKey);
    const hasEnoughBytes = storedBytes >= minNativeBytesForHint(hint);
    if (storedUrl && hasEnoughBytes) return true;
    if (
      hint.readyKey &&
      window.localStorage.getItem(hint.readyKey) === "1" &&
      hasEnoughBytes
    ) {
      return true;
    }
    return hasEnoughBytes;
  } catch {
    return false;
  }
}

function nativeUrlForReadyFile(key) {
  const url = IOS_LOCAL_MAP_URLS[key] || "";
  if (!url || !hasNativeLocalHint(key)) return "";
  try {
    if (!String(window.localStorage.getItem(key) || "").trim()) {
      window.localStorage.setItem(key, url);
    }
  } catch {
    // ignore
  }
  return url;
}

function clearForceRemoteIfNativeReady(forceKey, primaryKey) {
  try {
    const forceRemote = window.localStorage.getItem(forceKey) === "1";
    if (!forceRemote) return false;
    if (hasNativeLocalHint(primaryKey) && hasNativeLocalHint(LOCAL_PM_CONTOURS_KEY)) {
      window.localStorage.removeItem(forceKey);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

function readLocalUrl(key) {
  if (typeof window === "undefined") return "";
  try {
    if (window.localStorage.getItem(MAPS_CLEARED_LOCAL_KEY)) return "";
    const raw = String(window.localStorage.getItem(key) || "").trim();
    if (!raw) return nativeUrlForReadyFile(key);
    if (
      hasNativeMapRuntimeHint() &&
      IOS_LOCAL_MAP_URLS[key]
    ) {
      const nativeUrl = IOS_LOCAL_MAP_URLS[key];
      if (raw !== nativeUrl) {
        window.localStorage.setItem(key, nativeUrl);
      }
      return nativeUrl;
    }
    const isValid =
      /^https?:\/\//i.test(raw) ||
      /^capacitor:\/\//i.test(raw) ||
      /^file:\/\//i.test(raw) ||
      /^trekkin-map:\/\//i.test(raw) ||
      raw.startsWith("/_capacitor_file_") ||
      raw.includes("localhost/_capacitor_file_") ||
      /^\/(private\/)?var\//i.test(raw);
    if (isValid) return raw;
    if (/^\/DATA\//i.test(raw)) {
      try {
        window.localStorage.removeItem(key);
      } catch {
        // ignore
      }
    }
    return "";
  } catch {
    return "";
  }
}

function shouldUseRemoteSource(sourceMode = "auto") {
  if (sourceMode === "remote") return true;
  if (sourceMode === "local") return false;
  return typeof navigator === "undefined" || navigator.onLine !== false;
}

function preferredLiteUrl(sourceMode = "auto") {
  const local = readLocalUrl(LOCAL_PM_LITE_KEY);
  const forceRemote = (() => {
    try {
      if (clearForceRemoteIfNativeReady(FORCE_REMOTE_LITE_KEY, LOCAL_PM_LITE_KEY)) {
        return true;
      }
      return localStorage.getItem(FORCE_REMOTE_LITE_KEY) === "1";
    } catch {
      return false;
    }
  })();
  return local && !forceRemote && !shouldUseRemoteSource(sourceMode)
    ? local
    : LITE_PMTILES_URL;
}

function preferredProUrl(sourceMode = "auto") {
  const local = readLocalUrl(LOCAL_PM_PRO_KEY);
  const forceRemote = (() => {
    try {
      if (clearForceRemoteIfNativeReady(FORCE_REMOTE_PRO_KEY, LOCAL_PM_PRO_KEY)) {
        return true;
      }
      return localStorage.getItem(FORCE_REMOTE_PRO_KEY) === "1";
    } catch {
      return false;
    }
  })();
  return local && !forceRemote && !shouldUseRemoteSource(sourceMode)
    ? local
    : PRO_PMTILES_URL;
}

function preferredContoursUrl(sourceMode = "auto") {
  const local = readLocalUrl(LOCAL_PM_CONTOURS_KEY);
  const forceRemote = (() => {
    try {
      if (
        hasNativeLocalHint(LOCAL_PM_CONTOURS_KEY) &&
        (hasNativeLocalHint(LOCAL_PM_LITE_KEY) || hasNativeLocalHint(LOCAL_PM_PRO_KEY))
      ) {
        localStorage.removeItem(FORCE_REMOTE_LITE_KEY);
        localStorage.removeItem(FORCE_REMOTE_PRO_KEY);
        return false;
      }
      return (
        localStorage.getItem(FORCE_REMOTE_LITE_KEY) === "1" ||
        localStorage.getItem(FORCE_REMOTE_PRO_KEY) === "1"
      );
    } catch {
      return false;
    }
  })();
  return local && !forceRemote && !shouldUseRemoteSource(sourceMode)
    ? local
    : CONTOURS_PMTILES_URL;
}

function hasNativeBridge() {
  return hasNativeMapRuntimeHint();
}

function hasLocalMapFiles() {
  return (
    !!readLocalUrl(LOCAL_PM_LITE_KEY) ||
    !!readLocalUrl(LOCAL_PM_PRO_KEY) ||
    !!readLocalUrl(LOCAL_PM_CONTOURS_KEY)
  );
}

function preferredGlyphsUrl() {
  const online = typeof navigator === "undefined" ? true : navigator.onLine !== false;
  if (!online || hasNativeBridge() || hasLocalMapFiles()) return LOCAL_GLYPHS_URL;
  return "https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf";
}

export function readMapStylePref(user) {
  try {
    const runtime = sessionStorage.getItem(RUNTIME_MAP_STYLE_OVERRIDE_KEY);
    if (runtime) return runtime;
  } catch {
    // ignore
  }
  try {
    const raw = localStorage.getItem(MAP_STYLE_KEY);
    if (raw) return raw;
  } catch {
    // ignore
  }
  if (user?.mapStyle) return user.mapStyle;
  return null;
}

export function resolveMapStyle(pref, canUseLite = true, canUsePro = true, canUseOpenStreet = false) {
  const normalized = String(pref || "").toLowerCase();
  if (normalized === "openstreet") {
    return canUseOpenStreet ? "openstreet" : canUsePro ? "pro" : canUseLite ? "lite" : "pro";
  }
  if (normalized === "pro") return canUsePro ? "pro" : canUseLite ? "lite" : "pro";
  if (normalized === "lite") return canUseLite ? "lite" : canUsePro ? "pro" : "lite";
  if (canUsePro) return "pro";
  if (canUseLite) return "lite";
  return "pro";
}

let pmtilesProtocolRef = null;

class CachedRemotePmtilesSource {
  constructor(url) {
    this.url = url;
    this.remote = new FetchSource(url);
  }

  getKey() {
    return this.url;
  }

  cacheKey(offset, length) {
    const url = new URL(this.url);
    url.searchParams.set("__trekkin_range", `${offset}-${length}`);
    return url.toString();
  }

  async getBytes(offset, length, signal, etag) {
    const key = this.cacheKey(offset, length);
    if (typeof caches !== "undefined") {
      try {
        const cache = await caches.open(PMTILES_CACHE);
        const cached = await cache.match(key);
        if (cached) {
          const data = await cached.arrayBuffer();
          const lastUsed = Number(
            cached.headers.get(RANGE_CACHE_LAST_USED_HEADER) || 0
          );
          if (!Number.isFinite(lastUsed) || Date.now() - lastUsed > 5 * 60 * 1000) {
            try {
              const refreshedHeaders = new Headers(cached.headers);
              refreshedHeaders.set(RANGE_CACHE_LAST_USED_HEADER, String(Date.now()));
              cache
                .put(
                  key,
                  new Response(data.slice(0), { headers: refreshedHeaders })
                )
                .catch(() => {});
            } catch {}
          }
          touchMapCache();
          return {
            data,
            etag: cached.headers.get("x-trekkin-etag") || undefined,
            cacheControl:
              cached.headers.get("x-trekkin-cache-control") || undefined,
            expires: cached.headers.get("x-trekkin-expires") || undefined,
          };
        }
      } catch {
        // CacheStorage is optional in native WebViews.
      }
    }

    const result = await this.remote.getBytes(offset, length, signal, etag);
    if (typeof caches !== "undefined" && result?.data) {
      try {
        const headers = new Headers({
          "content-type": "application/octet-stream",
          "x-trekkin-cached-at": String(Date.now()),
        });
        headers.set(RANGE_CACHE_LAST_USED_HEADER, String(Date.now()));
        if (result.etag) headers.set("x-trekkin-etag", result.etag);
        if (result.cacheControl) {
          headers.set("x-trekkin-cache-control", result.cacheControl);
        }
        if (result.expires) headers.set("x-trekkin-expires", result.expires);
        const cache = await caches.open(PMTILES_CACHE);
        await cache.put(key, new Response(result.data.slice(0), { headers }));
        touchMapCache();
      } catch {
        // A resposta remota continua válida mesmo sem persistência do cache.
      }
    }
    return result;
  }
}

export function ensurePmtilesProtocol() {
  if (!pmtilesProtocolRef) {
    pmtilesProtocolRef = new Protocol();
    maplibregl.addProtocol("pmtiles", pmtilesProtocolRef.tile);
  }
  for (const remoteUrl of [
    LITE_PMTILES_URL,
    PRO_PMTILES_URL,
    CONTOURS_PMTILES_URL,
  ]) {
    if (!pmtilesProtocolRef.get(remoteUrl)) {
      pmtilesProtocolRef.add(
        new PMTiles(new CachedRemotePmtilesSource(remoteUrl))
      );
    }
  }
  const hasIosNativeBridge =
    typeof window !== "undefined" &&
    typeof window?.webkit?.messageHandlers?.nativeBridge?.postMessage === "function";
  if (isNative || hasIosNativeBridge) {
    for (const fileName of [
      "portugal-lite.pmtiles",
      "portugal-pro.pmtiles",
      "portugal-contours.pmtiles",
    ]) {
      const key = `native-map://${fileName}`;
      if (!pmtilesProtocolRef.get(key)) {
        pmtilesProtocolRef.add(new PMTiles(new NativePmtilesSource(fileName)));
      }
    }
  }
  return pmtilesProtocolRef;
}

export function buildLiteStyle({ overlay = false, sourceMode = "auto" } = {}) {
  const liteUrl = preferredLiteUrl(sourceMode);
  return {
    version: 8,
    glyphs: preferredGlyphsUrl(),
    sources: {
      portugal: {
        type: "vector",
        url: `pmtiles://${liteUrl}`,
        tileSize: 512,
        minzoom: PORTUGAL_PMTILES_MIN_ZOOM,
        maxzoom: 12,
        bounds: PORTUGAL_PMTILES_BOUNDS,
        attribution: "(c) OpenStreetMap contributors",
      },
    },
    layers: [
      {
        id: "background",
        type: "background",
        paint: overlay
          ? {
              "background-color": "rgba(0, 0, 0, 0)",
              "background-opacity": 0,
            }
          : { "background-color": "#f4f1e8" },
      },
      {
        id: "landcover",
        type: "fill",
        source: "portugal",
        "source-layer": "landcover",
        paint: {
          "fill-color": [
            "match",
            ["get", "class"],
            "wood",
            "#cfe3c7",
            "grass",
            "#dcebcf",
            "scrub",
            "#d7e3c2",
            "wetland",
            "#cfe0d3",
            "glacier",
            "#e9eef2",
            "#e4eadf",
          ],
          "fill-opacity": 0.85,
        },
      },
      {
        id: "landuse",
        type: "fill",
        source: "portugal",
        "source-layer": "landuse",
        paint: {
          "fill-color": [
            "match",
            ["get", "class"],
            "park",
            "#c6e6b8",
            "forest",
            "#bddcb5",
            "grass",
            "#d9eac7",
            "farmland",
            "#eee3c6",
            "recreation_ground",
            "#d6e9c5",
            "cemetery",
            "#d7e5cf",
            "residential",
            "#efe6d8",
            "industrial",
            "#e1dfdb",
            "commercial",
            "#e7e2d5",
            "hospital",
            "#efe0e0",
            "school",
            "#e6e7de",
            "#e9e6dc",
          ],
          "fill-opacity": 0.75,
        },
      },
      {
        id: "water",
        type: "fill",
        source: "portugal",
        "source-layer": "water",
        paint: {
          "fill-color": "#9fc6e7",
          "fill-opacity": 0.9,
        },
      },
      {
        id: "waterway",
        type: "line",
        source: "portugal",
        "source-layer": "waterway",
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "#8fb8dd",
          "line-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            6,
            0.5,
            10,
            1.2,
            12,
            2.0,
          ],
          "line-opacity": 0.9,
        },
      },
      {
        id: "boundary",
        type: "line",
        source: "portugal",
        "source-layer": "boundary",
        paint: {
          "line-color": "#c0c0c0",
          "line-width": 1,
          "line-dasharray": [2, 2],
        },
      },
      {
        id: "roads",
        type: "line",
        source: "portugal",
        "source-layer": "transportation",
        filter: ["!in", "class", "path", "track"],
        paint: {
          "line-color": [
            "match",
            ["get", "class"],
            "motorway",
            "#df6b4c",
            "trunk",
            "#df6b4c",
            "primary",
            "#f2a65a",
            "secondary",
            "#f1c86a",
            "tertiary",
            "#e5d097",
            "minor",
            "#d0d0d0",
            "path",
            "#b6b6b6",
            "#c7c7c7",
          ],
          "line-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            6,
            0.6,
            10,
            1.4,
            12,
            2.2,
          ],
          "line-opacity": 0.85,
        },
      },
      {
        id: "trails",
        type: "line",
        source: "portugal",
        "source-layer": "transportation",
        filter: ["in", "class", "path", "track"],
        paint: {
          "line-color": "#9a9a9a",
          "line-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            6,
            0.6,
            10,
            1.4,
            12,
            2.1,
          ],
          "line-dasharray": [1, 1.4],
          "line-opacity": 0.8,
        },
      },
      {
        id: "waterway_labels",
        type: "symbol",
        source: "portugal",
        "source-layer": "waterway",
        minzoom: 9,
        layout: {
          "text-field": ["get", "name"],
          "text-font": ["Noto Sans Regular"],
          "text-size": [
            "interpolate",
            ["linear"],
            ["zoom"],
            9,
            10,
            12,
            12,
          ],
          "symbol-placement": "line",
        },
        paint: {
          "text-color": "#4b7aa1",
          "text-halo-color": "#ffffff",
          "text-halo-width": 1,
        },
      },
      {
        id: "road_labels",
        type: "symbol",
        source: "portugal",
        "source-layer": "transportation_name",
        layout: {
          "text-field": ["get", "name"],
          "text-font": ["Noto Sans Regular"],
          "text-size": 11,
          "symbol-placement": "line",
        },
        paint: {
          "text-color": "#5a5a5a",
          "text-halo-color": "#ffffff",
          "text-halo-width": 1,
        },
      },
      {
        id: "place_labels",
        type: "symbol",
        source: "portugal",
        "source-layer": "place",
        layout: {
          "text-field": ["get", "name"],
          "text-font": ["Noto Sans Bold"],
          "text-size": [
            "interpolate",
            ["linear"],
            ["zoom"],
            6,
            10,
            10,
            14,
            12,
            16,
          ],
        },
        paint: {
          "text-color": "#2f2f2f",
          "text-halo-color": "#ffffff",
          "text-halo-width": 1,
        },
      },
    ],
  };
}

export function buildProStyle({ overlay = false, sourceMode = "auto" } = {}) {
  const proUrl = preferredProUrl(sourceMode);
  const contoursUrl = preferredContoursUrl(sourceMode);
  const contoursDisabled = (() => {
    if (typeof window === "undefined") return false;
    try {
      return sessionStorage.getItem(RUNTIME_DISABLE_CONTOURS_PRO_KEY) === "1";
    } catch {
      return false;
    }
  })();
  return {
    version: 8,
    glyphs: preferredGlyphsUrl(),
    sources: {
      portugal: {
        type: "vector",
        url: `pmtiles://${proUrl}`,
        tileSize: 512,
        minzoom: PORTUGAL_PMTILES_MIN_ZOOM,
        maxzoom: 15,
        bounds: PORTUGAL_PMTILES_BOUNDS,
        attribution: "(c) OpenStreetMap contributors",
      },
      ...(contoursDisabled
        ? {}
        : {
            contours: {
              type: "vector",
              url: `pmtiles://${contoursUrl}`,
              tileSize: 512,
              minzoom: PORTUGAL_PMTILES_MIN_ZOOM,
              maxzoom: 11,
              bounds: PORTUGAL_PMTILES_BOUNDS,
            },
          }),
    },
    layers: [
      {
        id: "background",
        type: "background",
        paint: overlay
          ? {
              "background-color": "rgba(0, 0, 0, 0)",
              "background-opacity": 0,
            }
          : { "background-color": "#f3f0e7" },
      },
      {
        id: "landcover",
        type: "fill",
        source: "portugal",
        "source-layer": "landcover",
        paint: {
          "fill-color": [
            "match",
            ["get", "class"],
            "wood",
            "#c8e0bf",
            "grass",
            "#d9ecc9",
            "scrub",
            "#d0e0bb",
            "wetland",
            "#cbe1d6",
            "glacier",
            "#e9eef2",
            "#e4eadf",
          ],
          "fill-opacity": overlay ? 0.16 : 0.9,
        },
      },
      {
        id: "landuse",
        type: "fill",
        source: "portugal",
        "source-layer": "landuse",
        paint: {
          "fill-color": [
            "match",
            ["get", "class"],
            "park",
            "#c4e8b6",
            "forest",
            "#bddcb5",
            "grass",
            "#d8ebc8",
            "farmland",
            "#efe2c1",
            "residential",
            "#efe6d8",
            "industrial",
            "#e1dfdb",
            "commercial",
            "#e7e2d5",
            "cemetery",
            "#d7e5cf",
            "#e9e6dc",
          ],
          "fill-opacity": overlay ? 0.14 : 0.8,
        },
      },
      {
        id: "water",
        type: "fill",
        source: "portugal",
        "source-layer": "water",
        paint: {
          "fill-color": "#9fc6e7",
          "fill-opacity": overlay ? 0.35 : 0.92,
        },
      },
      {
        id: "waterway",
        type: "line",
        source: "portugal",
        "source-layer": "waterway",
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "#7daed8",
          "line-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            6,
            0.6,
            12,
            1.8,
            16,
            2.8,
          ],
          "line-opacity": 0.95,
        },
      },
      {
        id: "aeroway",
        type: "line",
        source: "portugal",
        "source-layer": "aeroway",
        paint: {
          "line-color": "#c2c2c2",
          "line-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            10,
            0.6,
            16,
            1.8,
          ],
          "line-opacity": 0.75,
        },
      },
      {
        id: "railway",
        type: "line",
        source: "portugal",
        "source-layer": "transportation",
        filter: ["in", "class", "rail", "railway"],
        paint: {
          "line-color": "#7d7d7d",
          "line-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            8,
            0.6,
            16,
            2.0,
          ],
          "line-opacity": 0.8,
        },
      },
      {
        id: "roads",
        type: "line",
        source: "portugal",
        "source-layer": "transportation",
        filter: ["!in", "class", "path", "track", "rail", "railway"],
        paint: {
          "line-color": [
            "match",
            ["get", "class"],
            "motorway",
            "#df6b4c",
            "trunk",
            "#df6b4c",
            "primary",
            "#f2a65a",
            "secondary",
            "#f1c86a",
            "tertiary",
            "#e5d097",
            "minor",
            "#d0d0d0",
            "#c7c7c7",
          ],
          "line-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            6,
            0.6,
            12,
            2.2,
            16,
            4.2,
          ],
          "line-opacity": 0.82,
        },
      },
      {
        id: "trails",
        type: "line",
        source: "portugal",
        "source-layer": "transportation",
        filter: ["in", "class", "path", "track"],
        paint: {
          "line-color": "#5f6f62",
          "line-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            8,
            0.9,
            12,
            1.8,
            16,
            2.8,
          ],
          "line-dasharray": [2, 1.2],
          "line-opacity": 0.95,
        },
      },
      {
        id: "buildings",
        type: "fill",
        source: "portugal",
        "source-layer": "building",
        minzoom: 12,
        paint: {
          "fill-color": "#d8d0c3",
          "fill-outline-color": "#c1b8aa",
          "fill-opacity": overlay ? 0.32 : 0.82,
        },
      },
      ...(contoursDisabled
        ? []
        : [
            {
              id: "contours",
              type: "line",
              source: "contours",
              "source-layer": "contours",
              paint: {
                "line-color": "#8f7a5f",
                "line-width": [
                  "interpolate",
                  ["linear"],
                  ["zoom"],
                  5,
                  0.55,
                  7,
                  0.85,
                  14,
                  1.45,
                  16,
                  1.95,
                  19,
                  2.3,
                ],
                "line-opacity": 0.86,
              },
            },
          ]),
      ...(contoursDisabled
        ? []
        : [
            {
              id: "contours-major",
              type: "line",
              source: "contours",
              "source-layer": "contours",
              filter: [
                "==",
                ["%", ["to-number", ["coalesce", ["get", "elev"], ["get", "ele"]], 0], 250],
                0,
              ],
              paint: {
                "line-color": "#725d46",
                "line-width": [
                  "interpolate",
                  ["linear"],
                  ["zoom"],
                  5,
                  0.85,
                  7,
                  1.25,
                  14,
                  2.05,
                  16,
                  2.55,
                  19,
                  3.15,
                ],
                "line-opacity": 0.94,
              },
            },
            {
              id: "contours-labels",
              type: "symbol",
              source: "contours",
              "source-layer": "contours",
              minzoom: 8,
              layout: {
                "symbol-placement": "line",
                "text-field": [
                  "coalesce",
                  ["get", "elev"],
                  ["get", "ele"],
                  ["get", "height"],
                  ["get", "elevation"],
                ],
                "text-font": ["Noto Sans Regular"],
                "text-size": [
                  "interpolate",
                  ["linear"],
                  ["zoom"],
                  8,
                  11,
                  14,
                  13,
                  16,
                  14,
                  19,
                  15,
                ],
              },
              paint: {
                "text-color": "#5c4b38",
                "text-halo-color": "#f7f4ee",
                "text-halo-width": 1.4,
              },
            },
          ]),
      {
        id: "poi",
        type: "symbol",
        source: "portugal",
        "source-layer": "poi",
        minzoom: 11,
        layout: {
          "text-field": ["get", "name"],
          "text-font": ["Noto Sans Regular"],
          "text-size": [
            "interpolate",
            ["linear"],
            ["zoom"],
            11,
            10,
            16,
            13.5,
          ],
          "symbol-placement": "point",
        },
        paint: {
          "text-color": "#2f3a3f",
          "text-halo-color": "#ffffff",
          "text-halo-width": 1.2,
        },
      },
      {
        id: "road_labels",
        type: "symbol",
        source: "portugal",
        "source-layer": "transportation_name",
        layout: {
          "text-field": ["get", "name"],
          "text-font": ["Noto Sans Regular"],
          "text-size": 11.5,
          "symbol-placement": "line",
        },
        paint: {
          "text-color": "#4f4f4f",
          "text-halo-color": "#ffffff",
          "text-halo-width": 1.2,
        },
      },
      {
        id: "place_labels",
        type: "symbol",
        source: "portugal",
        "source-layer": "place",
        layout: {
          "text-field": ["get", "name"],
          "text-font": ["Noto Sans Bold"],
          "text-size": [
            "interpolate",
            ["linear"],
            ["zoom"],
            6,
            10,
            10,
            14,
            16,
            18,
          ],
        },
        paint: {
          "text-color": "#25323a",
          "text-halo-color": "#ffffff",
          "text-halo-width": 1.3,
        },
      },
    ],
  };
}
